import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Download, AlarmClock } from "lucide-react";
import api, { errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { php, phone, date } from "@/lib/format";
import { PageHeader, MarketSelect, PeopleSelect, SelectBox, StatusBadge, DemoTag, Empty } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import LeadFormDialog from "@/components/leads/LeadFormDialog";
import LeadDrawer from "@/components/leads/LeadDrawer";
import { dateRange, DateFilter } from "@/pages/Dashboard";

export default function Leads() {
  const { can, meta, marketName } = useAuth();
  const [tab, setTab] = useState("all");
  const [f, setF] = useState({ q: "", market: "", source: "", assigned_to: "", location: "" });
  const [preset, setPreset] = useState("");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [rows, setRows] = useState([]);
  const [create, setCreate] = useState(false);
  const [openId, setOpenId] = useState(null);

  const params = useCallback(() => {
    const p = { ...f, ...dateRange(preset, custom) };
    if (tab === "open" || tab === "won" || tab === "lost") p.status = tab;
    if (tab === "archived") p.archived = true;
    return Object.fromEntries(Object.entries(p).filter(([, v]) => v));
  }, [f, tab, preset, custom]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/leads", { params: params() });
      setRows(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [params]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });

  return (
    <div data-testid="leads-page">
      <PageHeader eyebrow="Central CRM" title="Leads & Customers" subtitle="Unified, searchable directory across all three markets. Won records stay here for follow-ups and repeat business.">
        {can("reports.export") && <Button variant="outline" onClick={() => downloadFile("/export/leads.csv", "salescore_leads.csv", params()).catch((e) => toast.error(errMsg(e)))} data-testid="leads-export-button"><Download className="h-4 w-4 mr-1" />Export CSV</Button>}
        {can("leads.create") && <Button onClick={() => setCreate(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="leads-add-button"><Plus className="h-4 w-4 mr-1" />New lead</Button>}
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="bg-white border">
          {[["all", "All active"], ["open", "Open"], ["won", "Customers (won)"], ["lost", "Lost"], ["archived", "Archived"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`leads-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" /><Input placeholder="Name, company, phone, email…" className="pl-8 w-64 bg-white" value={f.q} onChange={set("q")} data-testid="leads-search-input" /></div>
        <MarketSelect value={f.market} onChange={set("market")} />
        <SelectBox value={f.source} onChange={set("source")} testid="source-filter" allLabel="All sources" options={(meta.settings.lead_sources || []).map((s) => ({ value: s, label: s }))} />
        {can("leads.view_all") && <PeopleSelect value={f.assigned_to} onChange={set("assigned_to")} includeUnassigned />}
        <Input placeholder="Location" className="w-40 bg-white" value={f.location} onChange={set("location")} data-testid="location-filter" />
        <DateFilter preset={preset} setPreset={setPreset} custom={custom} setCustom={setCustom} />
      </div>

      <div className="bg-white border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name / Company</TableHead><TableHead>Market</TableHead><TableHead>Stage</TableHead><TableHead>Contact</TableHead>
              <TableHead>Location</TableHead><TableHead>Source</TableHead><TableHead>Assigned</TableHead><TableHead className="text-right">Value</TableHead><TableHead>Follow-up</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((l) => (
              <TableRow key={l.id} className="cursor-pointer" onClick={() => setOpenId(l.id)} data-testid={`lead-row-${l.id}`}>
                <TableCell><div className="font-medium">{l.name}<DemoTag show={l.is_demo} /></div><div className="text-xs text-slate-500">{l.company}</div></TableCell>
                <TableCell className="text-xs">{marketName(l.market)}</TableCell>
                <TableCell><div className="text-sm">{l.stage_name}</div><StatusBadge status={l.status} /></TableCell>
                <TableCell className="text-xs whitespace-nowrap"><div>{phone(l.phone)}</div><div className="text-slate-500">{l.email}</div></TableCell>
                <TableCell className="text-xs">{l.location}</TableCell>
                <TableCell className="text-xs">{l.source}</TableCell>
                <TableCell className="text-xs">{l.assigned_name || <span className="text-slate-400">Unassigned</span>}</TableCell>
                <TableCell className="text-right num text-sm whitespace-nowrap">{php(l.status === "won" ? l.won_value : l.value)}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{l.next_follow_up ? <span className={`flex items-center gap-1 ${l.overdue ? "text-red-600 font-semibold" : ""}`}><AlarmClock className="h-3 w-3" />{date(l.next_follow_up)}</span> : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {rows.length === 0 && <Empty testid="leads-empty">No records match these filters.</Empty>}
      </div>
      <div className="text-xs text-slate-400 mt-2" data-testid="leads-count">{rows.length} record(s)</div>

      <LeadFormDialog open={create} onOpenChange={setCreate} onSaved={load} />
      <LeadDrawer leadId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
